import { onUnauthenticated, setApiLocale } from '@ottd/api';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Toaster } from '@/components/ui/sonner';
import { activeLocale } from '@/lib/locale';

/*
 * Cross-cutting API/UI wiring: mounts the sonner Toaster, keeps the API
 * client's `Accept-Language` in step with the UI language, and surfaces a
 * single "session expired" toast when a request 401/419s on a browser that
 * actually held a session (the AuthProvider already flips the session to
 * guest). The @ottd/api client itself is configured at module load in main.tsx
 * so config lands before the first query.
 */

/** How long one expiry toast suppresses a storm of concurrent 401s. */
const TOAST_DEBOUNCE_MS = 5_000;

export function ApiClientProvider({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation('auth');
  const queryClient = useQueryClient();

  // Cached responses carry the language they were fetched in, so a switch has
  // to both re-point the client and drop what the previous language cached.
  useEffect(() => {
    const syncLocale = () => {
      setApiLocale(activeLocale());
      void queryClient.invalidateQueries();
    };

    i18n.on('languageChanged', syncLocale);
    return () => {
      i18n.off('languageChanged', syncLocale);
    };
  }, [i18n, queryClient]);

  useEffect(() => {
    let notified = false;
    const unsubscribe = onUnauthenticated(({ expired }) => {
      // Every guest's first paint 401s on the GET /api/auth/me probe. That is
      // the normal logged-out path, not an expiry - telling a first-time
      // visitor their session ran out is nonsense.
      if (!expired) return;
      // Debounce a storm of concurrent 401s into one toast.
      if (notified) return;
      notified = true;
      toast.error(t('errors.sessionExpired'));
      window.setTimeout(() => {
        notified = false;
      }, TOAST_DEBOUNCE_MS);
    });
    return unsubscribe;
  }, [t]);

  return (
    <>
      {children}
      <Toaster />
    </>
  );
}
